import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Skeleton,
  Stack,
  SvgIcon,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import { useNavigate } from 'react-router-dom';
import { DjPageShell } from '@/shared/ui/DjPageShell';
import { StatusChip } from '@/shared/ui/StatusChip';
import { EmptyState } from '@/shared/ui/EmptyState';
import { useAuth } from '@/shared/auth/useAuth';
import { eventApi } from '@/entities/event/api/event.api';
import type { DjEvent, } from '@/entities/event/model/event.types';
import type { DjStats } from '@/entities/event/api/event.api';
import { routePaths } from '@/app/router/routePaths';

const iconPaths = {
  arrow: 'M12 4l-1.41 1.41L15.17 10H4v2h11.17l-4.58 4.59L12 18l7-7-7-7z',
  music: 'M12 3v11.55A4 4 0 1 0 14 18V7h6V3h-8zM8 20a2 2 0 1 1 0-4 2 2 0 0 1 0 4z',
  headphones: 'M12 3a8 8 0 0 0-8 8v6a3 3 0 0 0 3 3h2v-8H6v-1a6 6 0 0 1 12 0v1h-3v8h2a3 3 0 0 0 3-3v-6a8 8 0 0 0-8-8z',
  wave: 'M4 10h2v8H4v-8zm4-4h2v12H8V6zm4-3h2v15h-2V3zm4 6h2v9h-2V9zm4-4h2v13h-2V5z',
  bolt: 'M11 21h-1l1-7H7.5a1 1 0 0 1-.9-1.45l5-10A1 1 0 0 1 13.5 3h1l-1 7H17a1 1 0 0 1 .9 1.45l-5 10A1 1 0 0 1 11 21z',
  plus: 'M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z',
  star: 'M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z',
  spotify: 'M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm4.586 14.424a.623.623 0 0 1-.857.207c-2.348-1.435-5.304-1.76-8.785-.964a.623.623 0 1 1-.277-1.215c3.809-.87 7.077-.496 9.712 1.115a.623.623 0 0 1 .207.857zm1.223-2.722a.78.78 0 0 1-1.072.257c-2.687-1.652-6.785-2.131-9.965-1.166a.78.78 0 0 1-.973-.519.78.78 0 0 1 .518-.973c3.632-1.102 8.147-.568 11.234 1.329a.78.78 0 0 1 .258 1.072zm.105-2.835C14.692 8.95 9.375 8.775 6.297 9.71a.937.937 0 1 1-.543-1.795c3.532-1.07 9.404-.862 13.115 1.338a.936.936 0 0 1 .045 1.614z',
};

const waveform = [28, 44, 18, 56, 34, 62, 22, 50, 38, 58, 26, 46, 32, 54, 20, 42, 30, 48];

function AppIcon({ path, ...props }: SvgIconProps & { path: string }) {
  return <SvgIcon {...props}><path d={path} /></SvgIcon>;
}

function GlassCard({ children, sx = {}, onClick }: { children: React.ReactNode; sx?: object; onClick?: () => void }) {
  const theme = useTheme();
  return (
    <Box
      onClick={onClick}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        p: { xs: 2, sm: 2.5 },
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
        bgcolor: alpha(theme.palette.common.white, 0.065),
        backdropFilter: 'blur(20px)',
        boxShadow: `0 20px 46px ${alpha(theme.palette.common.black, 0.16)}`,
        cursor: onClick ? 'pointer' : 'default',
        transition: onClick ? 'transform 0.18s, background-color 0.18s, border-color 0.18s, box-shadow 0.18s' : undefined,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: `linear-gradient(90deg, ${theme.palette.warning.light}, ${theme.palette.secondary.light}, ${theme.palette.primary.light})`,
          opacity: 0.72,
        },
        '&:hover': onClick ? {
          bgcolor: alpha(theme.palette.common.white, 0.095),
          borderColor: alpha(theme.palette.secondary.light, 0.28),
          transform: 'translateY(-2px)',
          boxShadow: `0 22px 54px ${alpha(theme.palette.common.black, 0.28)}`,
        } : undefined,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function StatSkeleton() {
  return (
    <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
      <Skeleton variant="text" width={80} height={16} />
      <Skeleton variant="text" width={48} height={48} sx={{ mt: 0.5 }} />
      <Skeleton variant="text" width={100} height={14} sx={{ mt: 0.5 }} />
    </Box>
  );
}

function formatEventDate(eventDate?: string): string {
  if (!eventDate) return 'No date set';
  const d = new Date(eventDate + 'T00:00:00');
  return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function DjDashboardPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [events, setEvents] = useState<DjEvent[]>([]);
  const [djStats, setDjStats] = useState<DjStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([eventApi.listDjEvents(), eventApi.getDjStats()])
      .then(([eventsData, statsData]) => {
        const statusOrder: Record<string, number> = { ACTIVE: 0, PENDING: 1, CLOSED: 2, ARCHIVED: 3 };
        const sorted = [...eventsData].filter((e) => e.status !== 'CLOSED' && e.status !== 'ARCHIVED').sort((a, b) => {
          const sd = (statusOrder[a.status] ?? 2) - (statusOrder[b.status] ?? 2);
          if (sd !== 0) return sd;
          const aT = a.eventDate ? new Date(`${a.eventDate}T${a.startTime ?? '00:00'}Z`).getTime() : Infinity;
          const bT = b.eventDate ? new Date(`${b.eventDate}T${b.startTime ?? '00:00'}Z`).getTime() : Infinity;
          return aT - bT;
        });
        setEvents(sorted);
        setDjStats(statsData);
      })
      .finally(() => setLoading(false));
  }, []);

  const firstName = user?.firstName ?? user?.displayName?.split(' ')[0] ?? 'DJ';
  const recentEvents = events.slice(0, 6);

  const stats = [
    {
      label: 'Active Events',
      value: loading ? null : String(djStats?.activeEvents ?? 0),
      sub: !djStats?.activeEvents ? 'No live sets right now' : djStats.activeEvents === 1 ? '1 live set' : `${djStats.activeEvents} live`,
      icon: iconPaths.wave,
      color: 'success.light',
    },
    {
      label: 'Song Requests',
      value: loading ? null : String(djStats?.pendingRequests ?? 0),
      sub: !djStats?.pendingRequests ? 'No pending requests' : `${djStats.pendingRequests} waiting on you`,
      icon: iconPaths.music,
      color: 'secondary.light',
    },
    {
      label: 'Guests Tuned In',
      value: loading ? null : String(djStats?.guestsTunedIn ?? 0),
      sub: !djStats?.guestsTunedIn ? 'No one in the room yet' : `${djStats.guestsTunedIn} in the crowd`,
      icon: iconPaths.headphones,
      color: 'primary.light',
    },
  ];

  const actions = [
    {
      label: 'Drop a New Set',
      sub: 'Create an event, generate a QR, let the crowd in.',
      icon: iconPaths.plus,
      color: 'secondary.main',
      href: routePaths.djCreateEvent,
    },
    {
      label: user?.spotifyConnected ? 'Manage Spotify' : 'Link Spotify',
      sub: user?.spotifyConnected
        ? 'View your connected playlists and link them to events.'
        : 'Connect your library and control the queue from the floor.',
      icon: iconPaths.spotify,
      color: 'primary.light',
      href: routePaths.djSpotify,
    },
  ];

  return (
    <DjPageShell>
      <Stack spacing={{ xs: 3.5, sm: 4.5 }}>

        {/* Hero greeting */}
        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            p: { xs: 2, sm: 2.5 },
            borderRadius: 2.25,
            border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
            bgcolor: alpha(theme.palette.common.white, 0.055),
            backdropFilter: 'blur(20px)',
            boxShadow: `0 24px 58px ${alpha(theme.palette.common.black, 0.2)}`,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -80,
              right: -130,
              width: 300,
              height: 210,
              pointerEvents: 'none',
              background: `linear-gradient(115deg, transparent 0 24%, ${alpha(theme.palette.secondary.light, 0.2)} 24% 36%, transparent 36% 52%, ${alpha(theme.palette.warning.light, 0.15)} 52% 62%, transparent 62%)`,
              transform: 'skewY(-8deg)',
            },
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'flex-end' }}
            justifyContent="space-between"
            spacing={2}
            sx={{ position: 'relative', mb: 1.5 }}
          >
            <Box>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
                <Chip
                  icon={<AppIcon path={iconPaths.bolt} />}
                  label="Console online"
                  size="small"
                  sx={{
                    height: 28,
                    color: 'success.light',
                    border: `1px solid ${alpha(theme.palette.success.light, 0.4)}`,
                    bgcolor: alpha(theme.palette.success.main, 0.12),
                    '& .MuiChip-icon': { color: 'success.light', fontSize: 14 },
                    '& .MuiChip-label': { px: 1, fontWeight: 800, fontSize: 12 },
                  }}
                />
              </Stack>
              <Typography
                variant="h3"
                sx={{
                  fontSize: { xs: 32, sm: 42, md: 52 },
                  fontWeight: 900,
                  lineHeight: 1.05,
                  color: 'common.white',
                  letterSpacing: 0,
                }}
              >
                What's the vibe,{' '}
                <Box
                  component="span"
                  sx={{
                    background: `linear-gradient(90deg, ${theme.palette.secondary.light}, ${theme.palette.warning.light})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  {firstName}?
                </Box>
              </Typography>
              <Typography
                sx={{
                  mt: 1,
                  color: alpha(theme.palette.common.white, 0.6),
                  fontSize: { xs: 15, sm: 17 },
                  fontWeight: 500,
                  lineHeight: 1.5,
                }}
              >
                Your crowd is waiting. Build an event, share a link, own the floor.
              </Typography>
            </Box>

            
          </Stack>

          {/* Waveform decoration */}
          <Box aria-hidden="true" sx={{ position: 'relative', display: 'flex', alignItems: 'flex-end', gap: '4px', height: 54, mt: 2, overflow: 'hidden', opacity: 0.55 }}>
            {waveform.map((h, i) => (
              <Box
                key={i}
                sx={{
                  flex: '0 0 auto',
                  width: { xs: 6, sm: 8 },
                  height: h,
                  borderRadius: 99,
                  background:
                    i % 4 === 0
                      ? `linear-gradient(180deg, ${theme.palette.warning.light}, ${theme.palette.secondary.main})`
                      : i % 2 === 0
                        ? `linear-gradient(180deg, ${theme.palette.secondary.light}, ${theme.palette.primary.light})`
                        : `linear-gradient(180deg, ${theme.palette.primary.light}, ${alpha(theme.palette.primary.main, 0.5)})`,
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Stats row */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5 }}>
          {stats.map((stat) => (
            loading ? (
              <GlassCard key={stat.label}><StatSkeleton /></GlassCard>
            ) : (
              <GlassCard key={stat.label}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: alpha(theme.palette.common.white, 0.55), letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                      {stat.label}
                    </Typography>
                    <Typography sx={{ mt: 0.75, fontSize: { xs: 36, sm: 42 }, fontWeight: 900, lineHeight: 1, color: 'common.white' }}>
                      {stat.value}
                    </Typography>
                    <Typography sx={{ mt: 0.75, fontSize: 13, color: alpha(theme.palette.common.white, 0.45), fontWeight: 500 }}>
                      {stat.sub}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: 42, height: 42, borderRadius: 1.5, display: 'grid', placeItems: 'center',
                      bgcolor: alpha(theme.palette.common.white, 0.08),
                      border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
                      color: stat.color, flexShrink: 0,
                    }}
                  >
                    <AppIcon path={stat.icon} fontSize="small" />
                  </Box>
                </Stack>
              </GlassCard>
            )
          ))}
        </Box>

        {/* Quick actions */}
        <Box>
          <Typography sx={{ mb: 1.75, fontSize: 13, fontWeight: 700, color: alpha(theme.palette.common.white, 0.45), letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            Quick actions
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.5 }}>
            {actions.map((action) => (
              <Box
                key={action.label}
                onClick={() => navigate(action.href)}
                sx={{
                  p: { xs: 2.25, sm: 2.75 },
                  borderRadius: 1.5,
                  border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
                  bgcolor: alpha(theme.palette.common.white, 0.055),
                  backdropFilter: 'blur(18px)',
                  cursor: 'pointer',
                  transition: 'all 0.18s',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.common.white, 0.1),
                    border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
                    transform: 'translateY(-2px)',
                    boxShadow: `0 16px 40px ${alpha(theme.palette.common.black, 0.32)}`,
                  },
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Box
                      sx={{
                        width: 48, height: 48, borderRadius: 1.5, display: 'grid', placeItems: 'center',
                        bgcolor: alpha(theme.palette.common.white, 0.08),
                        border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
                        color: action.color,
                        boxShadow: `0 0 20px ${alpha(theme.palette.secondary.main, 0.2)}`,
                      }}
                    >
                      <AppIcon path={action.icon} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: { xs: 16, sm: 18 }, fontWeight: 900, color: 'common.white', lineHeight: 1.15 }}>
                        {action.label}
                      </Typography>
                      <Typography sx={{ mt: 0.5, fontSize: 13.5, color: alpha(theme.palette.common.white, 0.52), lineHeight: 1.4, maxWidth: 280 }}>
                        {action.sub}
                      </Typography>
                    </Box>
                  </Stack>
                  <AppIcon path={iconPaths.arrow} sx={{ color: alpha(theme.palette.common.white, 0.3), flexShrink: 0, ml: 1 }} />
                </Stack>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Recent events */}
        <Box>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.75 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: alpha(theme.palette.common.white, 0.45), letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              Active &amp; Upcoming
            </Typography>
            <Button
              onClick={() => navigate(routePaths.djEvents)}
              size="small"
              sx={{ color: alpha(theme.palette.common.white, 0.55), fontSize: 13, fontWeight: 700, '&:hover': { color: 'common.white', bgcolor: alpha(theme.palette.common.white, 0.08) } }}
            >
              View all
            </Button>
          </Stack>

          {loading ? (
            <Stack spacing={1}>
              {[...Array(3)].map((_, i) => (
                <GlassCard key={i}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" width="40%" height={22} />
                      <Skeleton variant="text" width="25%" height={16} sx={{ mt: 0.5 }} />
                    </Box>
                    <Skeleton variant="rounded" width={60} height={24} sx={{ borderRadius: 99 }} />
                  </Stack>
                </GlassCard>
              ))}
            </Stack>
          ) : recentEvents.length === 0 ? (
            <GlassCard>
              <EmptyState
                title="No sets yet"
                description="Create your first event to get the crowd going."
              />
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, pb: 1 }}>
                <Button
                  onClick={() => navigate(routePaths.djCreateEvent)}
                  variant="outlined"
                  size="small"
                  sx={{
                    color: alpha(theme.palette.common.white, 0.75),
                    borderColor: alpha(theme.palette.common.white, 0.22),
                    '&:hover': { borderColor: 'secondary.light', bgcolor: alpha(theme.palette.secondary.main, 0.14), color: 'common.white' },
                  }}
                >
                  Create event
                </Button>
              </Box>
            </GlassCard>
          ) : (
            <Stack spacing={1}>
              {recentEvents.map((ev) => (
                <GlassCard
                  key={ev.eventId}
                  onClick={() => navigate(routePaths.djEventDetail.replace(':eventId', ev.eventId))}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{ fontSize: 15, fontWeight: 800, color: 'common.white', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                      >
                        {ev.name}
                      </Typography>
                      <Typography sx={{ mt: 0.4, fontSize: 12.5, color: alpha(theme.palette.common.white, 0.45), fontWeight: 500 }}>
                        {formatEventDate(ev.eventDate)}{ev.venueName ? ` · ${ev.venueName}` : ''}
                      </Typography>
                    </Box>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
                      <StatusChip
                        status={ev.status.toLowerCase() as never}
                        label={ev.status}
                        size="small"
                      />
                      <AppIcon path={iconPaths.arrow} sx={{ color: alpha(theme.palette.common.white, 0.25), fontSize: 18 }} />
                    </Stack>
                  </Stack>
                </GlassCard>
              ))}
            </Stack>
          )}
        </Box>

      </Stack>
    </DjPageShell>
  );
}
